import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import PrintProformaInvoice from '@/components/print/PrintProformaInvoice';
import { useToast } from '@/contexts/ToastContext';
import { type MockProformaInvoice } from '@/mocks/billing';
import { proformaService } from '@/services/proformaService';
import { formatINR } from '@/utils/format';
import { asEntered } from '@/hooks/useUnits';

export default function ProformaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [pi, setPi] = useState<MockProformaInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [printOpen, setPrintOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [showConvertModal, setShowConvertModal] = useState(false);

  const fetchPI = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await proformaService.getProformaInvoice(id);
      setPi(data || null);
    } catch (e) {
      console.error('Failed to fetch proforma invoice from backend:', e);
      setPi(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPI();
  }, [fetchPI]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64 text-slate-400">
          <div className="text-center">
            <i className="ri-loader-4-line text-3xl animate-spin block mb-2" />
            <p className="text-sm">Loading proforma invoice details...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!pi) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64 text-slate-400">
          <div className="text-center">
            <i className="ri-bill-line text-4xl block mb-2" />
            <p className="text-lg font-medium">Proforma Invoice not found</p>
            <button
              onClick={() => navigate('/sales/proforma')}
              className="mt-3 text-indigo-600 hover:underline text-sm cursor-pointer"
            >
              Back to Proforma Invoices
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const daysRemaining = Math.max(0, Math.ceil((Date.parse(pi.validUntil) - Date.parse(today)) / 86400000));
  const isExpiringSoon = pi.status === 'SENT' && daysRemaining <= 7 && daysRemaining > 0;

  const STATUS_STYLES: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
    SENT: 'bg-blue-100 text-blue-700 border-blue-300',
    CONVERTED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    CANCELLED: 'bg-red-100 text-red-700 border-red-300',
    EXPIRED: 'bg-orange-100 text-orange-700 border-orange-300',
  };

  const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Draft',
    SENT: 'Sent to Customer',
    CONVERTED: 'Converted to Invoice',
    CANCELLED: 'Cancelled',
    EXPIRED: 'Expired',
  };

  const handleSend = async () => {
    try {
      await proformaService.updateStatus(pi.id, 'SENT');
      toast.success(`Proforma ${pi.piNumber} marked as sent`);
      fetchPI();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleCancel = async () => {
    try {
      await proformaService.updateStatus(pi.id, 'CANCELLED');
      toast.info(`Proforma ${pi.piNumber} cancelled`);
      fetchPI();
    } catch (e) {
      toast.error('Failed to cancel proforma');
    }
  };

  const handleEdit = () => {
    navigate('/sales/proforma/new', {
      state: {
        editMode: true,
        piId: pi.id,
      },
    });
  };

  const handleConvertToInvoice = () => {
    setShowConvertModal(true);
  };

  const handleConfirmConvert = () => {
    if (!pi) return;
    const raw = pi as any;
    navigate('/sales/invoices/new', {
      state: {
        fromProforma: {
          fromProforma: true,
          proformaId: pi.id,
          proformaInvoiceId: pi.id,
          piNumber: pi.piNumber || raw.pi_number,
          proformaNumber: pi.piNumber || raw.pi_number,
          customerId: pi.customerId || raw.customer_id,
          customerName: pi.customerName || raw.customer_name,
          customerGSTIN: pi.customerGSTIN || raw.customer_gstin,
          billingAddress: pi.billingAddress || raw.billing_address,
          shippingAddress: pi.shippingAddress || raw.shipping_address,
          warehouseId: pi.warehouseId || raw.warehouse_id,
          expectedAdvance: pi.expectedAdvance || raw.expected_advance,
          items: (pi.items || []).map((i: any) => ({
            itemId: i.itemId || i.item_id,
            itemCode: i.itemCode || i.item_code,
            itemName: i.itemName || i.item_name,
            hsnCode: i.hsnCode || i.hsn_code,
            qty: i.qty,
            unit: i.unit,
            rate: i.rate,
            enteredQty: (i as any).enteredQty ?? null,
            enteredUnitId: (i as any).enteredUnitId ?? null,
            enteredRate: (i as any).enteredRate ?? null,
            discount: i.discountPercent || i.discount || i.discount_percent,
            taxRate: i.taxRate || i.tax_rate,
          })),
        },
      },
    });
    setShowConvertModal(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  const flowSteps = [
    { label: 'Proforma Created', done: true },
    { label: 'Sent to Customer', done: pi.status === 'SENT' || pi.status === 'CONVERTED' },
    { label: 'Payment received (after invoice)', done: pi.status === 'CONVERTED' },
    { label: 'Converted to Invoice', done: pi.status === 'CONVERTED' },
  ];

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Back link */}
          <button
            onClick={() => navigate('/sales/proforma')}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line" /> Back to Proforma Invoices
          </button>

          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-700 border border-teal-200">
                      PROFORMA INVOICE
                    </span>
                  </div>
                  <h1 className="text-xl font-bold text-slate-800 flex items-center gap-1.5">
                    {pi.piNumber}
                  </h1>
                  <button
                    onClick={() => copyToClipboard(pi.piNumber)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer transition-colors"
                    title="Copy"
                  >
                    <i className="ri-file-copy-line text-sm" />
                  </button>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                      STATUS_STYLES[pi.status]
                    }`}
                  >
                    {STATUS_LABELS[pi.status]}
                  </span>
                </div>
                <div className="text-sm text-slate-600 space-y-0.5">
                  <p>
                    <span className="text-slate-400">Customer:</span> <strong>{pi.customerName}</strong>{' '}
                    {pi.customerGSTIN && <span className="text-xs text-slate-400 ml-2">GSTIN: {pi.customerGSTIN}</span>}
                  </p>
                  <p>
                    <span className="text-slate-400">Date:</span>{' '}
                    {new Date(pi.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                  <p>
                    <span className="text-slate-400">Valid Until:</span>{' '}
                    <span className={isExpiringSoon ? 'text-amber-600 font-semibold' : 'text-slate-700'}>
                      {new Date(pi.validUntil).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    {daysRemaining > 0 && (
                      <span
                        className={`ml-1.5 text-xs ${
                          isExpiringSoon ? 'text-amber-600 font-medium' : 'text-slate-400'
                        }`}
                      >
                        ({daysRemaining} day{daysRemaining > 1 ? 's' : ''} remaining)
                      </span>
                    )}
                  </p>
                  {pi.fromQuotationNumber && (
                    <p>
                      <span className="text-slate-400">From Quotation:</span>{' '}
                      <button
                        onClick={() => navigate(`/sales/quotations/${pi.fromQuotationId}`)}
                        className="text-indigo-600 hover:underline cursor-pointer font-medium"
                      >
                        {pi.fromQuotationNumber}
                      </button>
                    </p>
                  )}
                  {pi.preparedBy && <p><span className="text-slate-400">Prepared by:</span> {pi.preparedBy}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {pi.status === 'DRAFT' && (
                  <>
                    <button
                      onClick={handleSend}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-send-plane-line" /> Send to Customer
                    </button>
                    <button
                      onClick={handleEdit}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-indigo-200 text-sm font-medium text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-pencil-line" /> Edit
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-close-circle-line" /> Cancel
                    </button>
                  </>
                )}
                {pi.status === 'SENT' && (
                  <>
                    <button
                      onClick={handleConvertToInvoice}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-file-copy-line" /> Convert to Tax Invoice
                    </button>
                    <button
                      onClick={handleEdit}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-indigo-200 text-sm font-medium text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-pencil-line" /> Edit
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-close-circle-line" /> Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => setPrintOpen(true)}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-printer-line" /> Print
                </button>
              </div>
            </div>

            {/* Expiry Warning */}
            {isExpiringSoon && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2.5">
                <i className="ri-alert-line text-amber-600" />
                <span className="text-sm text-amber-700 font-medium">
                  This proforma expires in {daysRemaining} day{daysRemaining > 1 ? 's' : ''}.
                </span>
              </div>
            )}

            {/* Conversion Banner */}
            {pi.status === 'CONVERTED' && pi.convertedToInvoiceNumber && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-check-double-line text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium">
                    Converted to Invoice {pi.convertedToInvoiceNumber}
                  </span>
                </div>
                {pi.convertedToInvoiceId && (
                  <button
                    onClick={() => navigate('/sales/invoices')}
                    className="text-sm text-emerald-600 hover:underline cursor-pointer font-medium whitespace-nowrap"
                  >
                    View Invoice →
                  </button>
                )}
              </div>
            )}

            {/* Flow Guide */}
            <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Progress</h3>
              <div className="flex items-center gap-0">
                {flowSteps.map((step, idx) => (
                  <div key={step.label} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                          step.done
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'bg-white border-slate-300 text-slate-400'
                        }`}
                      >
                        {step.done ? <i className="ri-check-line" /> : idx + 1}
                      </div>
                      <span
                        className={`text-[10px] mt-1.5 text-center leading-tight ${
                          step.done ? 'text-emerald-700 font-medium' : 'text-slate-400'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < flowSteps.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 -mt-5 mx-0.5 ${step.done ? 'bg-emerald-400' : 'bg-slate-200'}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expected Advance + Balance Panel */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Payment Info</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Invoice Total</div>
                <div className="text-lg font-bold text-slate-800">{formatINR(pi.totalAmount)}</div>
              </div>
              <div className="text-center p-3 bg-teal-50 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Expected Advance</div>
                <div className="text-lg font-bold text-teal-600">{formatINR(pi.expectedAdvance || 0)}</div>
                {pi.expectedAdvance > 0 && (
                  <div className="text-[10px] text-teal-500 mt-1">Customer indicated</div>
                )}
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Balance Due</div>
                <div className="text-lg font-bold text-amber-600">{formatINR(pi.balanceDue || pi.totalAmount)}</div>
              </div>
            </div>
            <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-500 flex items-center gap-2">
              <i className="ri-information-line text-slate-400" />
              Advance payments are recorded in the Payments module after converting to Invoice.
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
            <div className="px-5 py-3 border-b border-slate-100">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap w-10">
                      #
                    </th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Item
                    </th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Description
                    </th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      HSN
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Qty
                    </th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Unit
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Rate
                    </th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Disc%
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Taxable
                    </th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      GST%
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pi.items.map((l) => asEntered(l)).map((item, idx) => (
                    <tr key={item.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="text-center px-4 py-3 text-slate-500 text-xs">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{item.itemName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{item.description || '—'}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-600">{item.hsnCode || '—'}</td>
                      <td className="text-right px-4 py-3 text-slate-700">{item.qty}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-500">{item.unit}</td>
                      <td className="text-right px-4 py-3 text-slate-700">{formatINR(item.rate)}</td>
                      <td className="text-center px-4 py-3 text-xs">
                        {item.discountPercent > 0 ? (
                          <span className="text-amber-600 font-medium">{item.discountPercent}%</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-right px-4 py-3 text-slate-700">{formatINR(item.taxableAmount)}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-600">{item.taxRate}%</td>
                      <td className="text-right px-4 py-3 font-medium text-slate-800">
                        {formatINR(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t border-slate-200 px-5 py-4 flex justify-end">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium text-slate-700">{formatINR(pi.subtotal)}</span>
                </div>
                {pi.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-medium text-red-600">-{formatINR(pi.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Taxable Amount</span>
                  <span className="font-medium text-slate-700">{formatINR(pi.taxableAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tax</span>
                  <span className="font-medium text-slate-700">{formatINR(pi.taxAmount)}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 flex justify-between">
                  <span className="font-bold text-slate-800">Grand Total</span>
                  <span className="font-bold text-slate-800 text-base">{formatINR(pi.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details Section */}
          {pi.bankDetails && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
              <div className="px-5 py-3 border-b border-slate-100">
                <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Bank Details</h2>
              </div>
              <div className="p-5">
                <pre className="text-sm text-slate-700 font-sans whitespace-pre-line">{pi.bankDetails}</pre>
              </div>
            </div>
          )}

          {/* Terms Section */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
            <button
              onClick={() => setTermsOpen(!termsOpen)}
              className="w-full flex items-center justify-between px-5 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Terms &amp; Details</h2>
              <i
                className={`text-slate-400 text-sm transition-transform duration-200 ${
                  termsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                }`}
              />
            </button>
            {termsOpen && (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {pi.paymentTerms && (
                  <div>
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Payment Terms</div>
                    <div className="text-sm text-slate-700">{pi.paymentTerms}</div>
                  </div>
                )}
                {pi.deliveryTerms && (
                  <div>
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Delivery Terms</div>
                    <div className="text-sm text-slate-700">{pi.deliveryTerms}</div>
                  </div>
                )}
                {pi.notes && (
                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Notes</div>
                    <div className="text-sm text-slate-700">{pi.notes}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Convert Confirmation Modal */}
        {showConvertModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowConvertModal(false)}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <h3 className="text-base font-bold text-slate-800">Convert to Tax Invoice</h3>
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-slate-700">
                  Convert <strong>{pi.piNumber}</strong> to Tax Invoice?
                </p>
                <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Customer</span>
                    <span className="font-medium text-slate-700">{pi.customerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Items</span>
                    <span className="font-medium text-slate-700">{pi.items.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total</span>
                    <span className="font-medium text-slate-700">{formatINR(pi.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expected Advance</span>
                    <span className="font-medium text-teal-600">{formatINR(pi.expectedAdvance || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1">
                    <span className="text-slate-500">Balance Due</span>
                    <span className="font-medium text-amber-600">
                      {formatINR(pi.balanceDue || pi.totalAmount)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <strong>Note:</strong> Converting will create a tax invoice, reduce stock, create GST liability, and mark this proforma as converted. This cannot be reversed.
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmConvert}
                  className="h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Convert to Invoice
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Modal */}
        {printOpen && <PrintProformaInvoice proforma={pi} onClose={() => setPrintOpen(false)} />}
      </div>
    </AppLayout>
  );
}